import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from '@stencil/core';
import DOMPurify from 'dompurify';

import { ClassNames, LhToastClose, LhToastUndo } from '@/src/utils/interfaces';

@Component({
  tag: 'patternlib-toast-item',
  styleUrl: 'patternlib-toast-item.scss',
  shadow: true,
})
export class PatternlibToastItem {
  @Element() hostElement: HTMLPatternlibToastItemElement;
  private toastWrapperRef: HTMLDivElement;

  /**
   * Set the position of the toast to animate to
   */
  @Prop() animationValue?: string;

  /**
   * Auto hide the toast
   */
  @Prop() autoHide?: boolean = false;

  /**
   * Delay hiding the toast (ms)
   */
  @Prop() delay?: number = 3000;

  /**
   * Set the toasts unique id
   */
  @Prop() itemId: string | number;

  /**
   * Set the toast message
   */
  @Prop() message?: string;

  /**
   * Set the subline message
   */
  @Prop() subline = '';

  /**
   * Set the position of the toast
   */
  @Prop() position?: 'top-left' | 'top-right' | 'top-center' | 'bottom-left' | 'bottom-right' | 'bottom-center' =
    'bottom-left';

  /**
   * Show the toast message
   */
  @Prop() show?: boolean = false;

  /**
   * Show icon on the left side of the toast.
   */
  @Prop() showIcon?: boolean = false;

  /**
   * Show an undo button
   */
  @Prop() showUndo?: boolean = false;

  /**
   * Define the toast type
   */
  @Prop() type?: 'default' | 'error' | 'success' | 'warning' = 'default';

  /**
   * Set the undo button label
   */
  @Prop() undoLabel?: string = 'undo';

  /**
   * Set the toast height
   */
  @Prop() height?: string = 'auto';

  /**
   * Set the toast width
   */
  @Prop() width?: string = '408px';

  /**
   * Slot for custom content
   */
  @Prop() customContent: string;
  /**
   * Monitoring width property
   */
  @Watch('width')
  widthWatcher(): void {
    this.hostElement.style.setProperty('--width', this.width);
  }

  /**
   * Monitoring animationValue
   */
  @Watch('animationValue')
  animationValuePropHandler(): void {
    this.hostElement.style.setProperty('--animation-value', this.animationValue);
  }

  /**
   * Internal state if toast is visible
   */
  @State() isVisible = this.show;

  /**
   * Provide a method to be triggered if the toast gets closed
   */
  @Event({ eventName: 'lhToastItemClose' }) toastItemClose: EventEmitter<LhToastClose>;

  /**
   * Provide a method to be triggered if the undo button gets clicked
   */
  @Event({ eventName: 'lhToastItemUndoClick' }) toastItemUndoClick: EventEmitter<LhToastUndo>;

  /**
   * Provide a method to be triggered when the toast item is rendered
   */
  @Event({ eventName: 'lhToastItemRendered' }) toastItemRendered: EventEmitter;

  private handleToastClose = () => async (): Promise<void> => {
    this.toastItemClose.emit({ itemId: this.itemId });
  };

  private handleToastUndo = () => async (): Promise<void> => {
    this.toastItemUndoClick.emit({ itemId: this.itemId });
  };

  componentWillLoad(): void {
    this.hostElement.style.setProperty('--animation-value', this.animationValue);
    this.hostElement.style.setProperty('--width', this.width);
  }

  componentDidRender(): void {
    this.toastItemRendered.emit({ itemId: this.itemId, domRef: this.toastWrapperRef });
  }

  render() {
    const icon = (
      <div>
        {this.showIcon && (
          <patternlib-multicolor-icon
            icon-name={this.type === 'default' ? 'info-filled-white-light' : `${this.type}-filled-white-light`}
            display="flex"
            class="icon-subinfo"
          />
        )}
      </div>
    );

    const toastWrapperClass: ClassNames = {
      'toastWrapper': true,
      'show': this.isVisible,
      'min-w-[140px] min-h-[56px] px-[24px] py-[12px]': true,
      'select-none cursor-default invisible fixed z-[1500]': true,
      [this.type]: true,
      [this.position]: true,
      [window.innerWidth < 500 ? 'mobile' : 'desktop_tablet']: true,
    };

    const messageWrapperClass: ClassNames = {
      'font-text-bold': true,
      'pl-[8px]': true,
      'py-[3px]': !(this.subline.length > 0),
    };

    const toastWrapperStyle = {
      width: window.innerWidth > 500 ? this.width : `${window.innerWidth - 40}px`,
      height: this.height,
    };

    const hasCustomContent = this.customContent?.length > 0;

    const sanitizedCustomContent = DOMPurify.sanitize(this.customContent);

    return (
      <Host>
        <div
          class={toastWrapperClass}
          style={toastWrapperStyle}
          ref={(el): HTMLDivElement => (this.toastWrapperRef = el)}
        >
          <div class="w-full h-full flex">
            {/* icon */}
            {this.showIcon && icon}

            <div class="w-full flex justify-between gap-x-2">
              {/* text container */}
              <div class="flex flex-col text-copy-small-uniform overflow-hidden overflow-ellipsis" title={this.message}>
                {hasCustomContent && <div class="pl-[8px]" innerHTML={sanitizedCustomContent}></div>}
                {!hasCustomContent && (
                  <div>
                    {this.message && (
                      <div class={messageWrapperClass} data-testid="messageDiv">
                        {this.message}
                      </div>
                    )}
                    {this.subline && (
                      <div data-testid="sublineDiv" class="sub-content">
                        {this.subline}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* action */}
              <div
                class="flex justify-end items-center cursor-pointer"
                onClick={this.showUndo ? this.handleToastUndo() : this.handleToastClose()}
              >
                {this.showUndo && (
                  <button class="button-undo" data-testid="undoButton">
                    {this.undoLabel}
                  </button>
                )}
                {!this.showUndo && <patternlib-icon iconName="x" size="24px"></patternlib-icon>}
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
